/* eslint-disable max-lines */
import {
    Alert, AlertDescription
} from 'shared/components/ui/alert';
import {
    Button
} from 'shared/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
    Label
} from 'shared/components/ui/label';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Input
} from 'shared/components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import {
    AlertCircle, Wand2, Plus, Trash2
} from 'lucide-react';
import React, {
    useState, useEffect
} from 'react';
import {
    generateBodyFromSchema
} from './schemaUtils';

export default function TestApiDialog({
    open,
    setOpen,
    onTest,
    api
}) {
    const [activeTab, setActiveTab] = useState('params');
    const [testData, setTestData] = useState({
        paramsDef: [],
        headers: {},
        auth: {
            type: 'none',
            token: '',
            username: '',
            password: ''
        },
        body: ''
    });
    const [jsonError, setJsonError] = useState('');

    // Initialize test data from API when dialog opens
    useEffect(() => {
        if (open && api) {
            // Set initial values from API
            const initialData = {
                paramsDef: api.paramsDef ? [...api.paramsDef] : [],
                headers: api.headers ? {
                    ...api.headers
                } : {},
                auth: api.auth ? {
                    ...api.auth
                } : {
                    type: 'none',
                    token: '',
                    username: '',
                    password: ''
                },
                body: ''
            };

            // Auto-generate body from schema if available
            if (api.input_schema && api.method !== 'GET') {
                const generatedBody = generateBodyFromSchema(api.input_schema);
                if (generatedBody) {
                    initialData.body = generatedBody;
                }
            }

            setTestData(initialData);
            setJsonError('');

            // Set default active tab based on method
            if (api.method === 'GET') {
                setActiveTab('params');
            } else {
                setActiveTab('body');
            }
        }
    }, [open, api]);

    // Query Params handlers
    const addParams = () => {
        setTestData({
            ...testData,
            paramsDef: [...testData.paramsDef, {
                key: '',
                value: ''
            }]
        });
    };

    const updateQueryParams = (index, field, value) => {
        const updatedParams = [...testData.paramsDef];
        updatedParams[index] = {
            ...updatedParams[index],
            [field]: value
        };
        setTestData({
            ...testData,
            paramsDef: updatedParams
        });
    };

    const removeQueryParams = (index) => {
        const updatedParams = [...testData.paramsDef];
        updatedParams.splice(index, 1);
        setTestData({
            ...testData,
            paramsDef: updatedParams
        });
    };

    // Headers handlers
    const addHeader = () => {
        setTestData({
            ...testData,
            headers: {
                ...testData.headers,
                '': ''
            }
        });
    };

    const updateHeader = (key, newValue) => {
        const updated = {
            ...testData.headers,
            [key]: newValue
        };
        setTestData({
            ...testData,
            headers: updated
        });
    };

    const updateHeaderKey = (oldKey, newKey) => {
        if (newKey && newKey !== oldKey) {
            const {
                [oldKey]: value, ...rest
            } = testData.headers;
            const updated = {
                ...rest,
                [newKey]: value
            };
            setTestData({
                ...testData,
                headers: updated
            });
        }
    };

    const removeHeader = (key) => {
        const updated = {
            ...testData.headers
        };
        delete updated[key];
        setTestData({
            ...testData,
            headers: updated
        });
    };

    // Auth handlers
    const handleAuthTypeChange = (value) => {
        setTestData({
            ...testData,
            auth: {
                ...testData.auth,
                type: value
            }
        });
    };

    const handleAuthValueChange = (e) => {
        const {
            name, value
        } = e.target;
        setTestData({
            ...testData,
            auth: {
                ...testData.auth,
                [name]: value
            }
        });
    };

    // Body handlers
    const handleBodyChange = (e) => {
        const value = e.target.value;
        setTestData({
            ...testData,
            body: value
        });

        if (value.trim()) {
            try {
                JSON.parse(value);
                setJsonError('');
            } catch (error) {
                setJsonError(error.message);
            }
        } else {
            setJsonError('');
        }
    };

    const handleGenerateFromSchema = () => {
        if (api?.input_schema) {
            const generatedBody = generateBodyFromSchema(api.input_schema);
            if (generatedBody) {
                setTestData({
                    ...testData,
                    body: generatedBody
                });
                setJsonError('');
            }
        }
    };

    const handleTest = () => {
        if (jsonError) return;

        onTest(testData);
        setOpen(false);
    };

    const handleCancel = () => {
        setOpen(false);
        setTestData({
            paramsDef: [],
            headers: {},
            auth: {
                type: 'none',
                token: '',
                username: '',
                password: ''
            },
            body: ''
        });
        setJsonError('');
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="w-full max-w-4xl h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>
                        <h2 className="text-xl font-semibold">Test API: {api?.name}</h2>
                    </DialogTitle>
                </DialogHeader>

                <Tabs className="flex-1 flex flex-col overflow-hidden" value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-4 mb-4 flex-shrink-0">
                        <TabsTrigger value="params">Query Params</TabsTrigger>
                        <TabsTrigger value="headers">Headers</TabsTrigger>
                        <TabsTrigger value="auth">Authorization</TabsTrigger>
                        <TabsTrigger disabled={api?.method === 'GET'} value="body">Body</TabsTrigger>
                    </TabsList>

                    <div className="flex-1 overflow-y-auto">
                        {/* Query Params Tab */}
                        <TabsContent value="params" className="space-y-4 mt-0">
                            <div className="flex justify-between items-center">
                                <h3 className="text-sm font-semibold">Query Parameters</h3>
                                <Button type="button" variant="outline" size="sm" onClick={addParams}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Param
                                </Button>
                            </div>

                            {testData.paramsDef.length === 0 ? (
                                <div className="text-center p-4 border border-dashed rounded-md">
                                    <p className="text-sm text-muted-foreground">No parameters added yet</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {testData.paramsDef.map((param, index) => (
                                        <div key={index} className="flex gap-2 items-center">
                                            <Input
                                                value={param.key}
                                                onChange={(e) => updateQueryParams(index, 'key', e.target.value)}
                                                placeholder="Param name"
                                                className="flex-1"
                                            />
                                            <Input
                                                value={param.value}
                                                onChange={(e) => updateQueryParams(index, 'value', e.target.value)}
                                                placeholder="Value"
                                                className="flex-1"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeQueryParams(index)}
                                                className="h-9 w-9 text-destructive hover:text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>

                        {/* Headers Tab */}
                        <TabsContent value="headers" className="space-y-4 mt-0">
                            <div className="flex justify-between items-center">
                                <h3 className="text-sm font-semibold">Request Headers</h3>
                                <Button type="button" variant="outline" size="sm" onClick={addHeader}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Header
                                </Button>
                            </div>

                            {Object.keys(testData.headers).length === 0 ? (
                                <div className="text-center p-4 border border-dashed rounded-md">
                                    <p className="text-sm text-muted-foreground">No headers added yet</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {Object.entries(testData.headers).map(([key, value], index) => (
                                        <div key={index} className="flex gap-2 items-center">
                                            <Input
                                                value={key}
                                                onChange={(e) => updateHeaderKey(key, e.target.value)}
                                                placeholder="Header name"
                                                className="flex-1"
                                            />
                                            <Input
                                                value={value}
                                                onChange={(e) => updateHeader(key, e.target.value)}
                                                placeholder="Value"
                                                className="flex-1"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeHeader(key)}
                                                className="h-9 w-9 text-destructive hover:text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>

                        {/* Authorization Tab */}
                        <TabsContent value="auth" className="space-y-4 mt-0">
                            <div className="space-y-2">
                                <Label htmlFor="auth-type">Authorization Type</Label>
                                <Select value={testData.auth.type} onValueChange={handleAuthTypeChange}>
                                    <SelectTrigger id="auth-type">
                                        <SelectValue placeholder="Select authorization type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        <SelectItem value="bearer">Bearer Token</SelectItem>
                                        <SelectItem value="basic">Basic Auth</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {testData.auth.type === 'bearer' && (
                                <div className="space-y-2">
                                    <Label htmlFor="token">Bearer Token</Label>
                                    <Input
                                        id="token"
                                        name="token"
                                        value={testData.auth.token}
                                        onChange={handleAuthValueChange}
                                        placeholder="Enter your token"
                                    />
                                </div>
                            )}

                            {testData.auth.type === 'basic' && (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="username">Username</Label>
                                        <Input
                                            id="username"
                                            name="username"
                                            value={testData.auth.username}
                                            onChange={handleAuthValueChange}
                                            placeholder="Username"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="password">Password</Label>
                                        <Input
                                            id="password"
                                            name="password"
                                            type="password"
                                            value={testData.auth.password}
                                            onChange={handleAuthValueChange}
                                            placeholder="Password"
                                        />
                                    </div>
                                </div>
                            )}
                        </TabsContent>

                        {/* Body Tab */}
                        <TabsContent value="body" className="space-y-4 mt-0">
                            <div className="space-y-1">
                                <div className="flex justify-between items-center">
                                    <Label htmlFor="body">Request Body <small>(optional)</small></Label>
                                    {api?.input_schema && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleGenerateFromSchema}
                                        >
                                            <Wand2 className="h-4 w-4 mr-2" />
                                            Generate from Schema
                                        </Button>
                                    )}
                                </div>
                                {api?.input_schema && (
                                    <p className="text-xs text-muted-foreground text-right">
                                        Fills in default values from schema and sets empty values for other fields
                                    </p>
                                )}
                            </div>
                            <Textarea
                                placeholder={'{\n  "key": "value"\n}'}
                                id="body"
                                value={testData.body}
                                onChange={handleBodyChange}
                                className="font-mono h-64"
                            />
                            {jsonError && (
                                <Alert variant="destructive" className="mt-2">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>Invalid JSON: {jsonError}</AlertDescription>
                                </Alert>
                            )}
                        </TabsContent>
                    </div>
                </Tabs>

                <div className="flex justify-end mt-4 items-center gap-3 flex-shrink-0">
                    <Button
                        variant="outline"
                        type="button"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={jsonError}
                        type="button"
                        onClick={handleTest}
                    >
                        Test API
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
